import { Zap } from 'lucide-react'
import { Badge, Button } from '@waago/ui'
import { devCodeAvailable } from '@waago/api'

/**
 * One-click sign-in for development.
 *
 * <p>Rendered only when the API is pointed at a local backend — the accounts
 * behind these buttons are seeded by the `local` and `ci` Spring profiles and
 * do not exist anywhere else. Shipping a visible one-click admin login to
 * production would be an open door, so the guard is on the build mode rather
 * than on a runtime flag someone can flip.
 */
export type DemoAccount = {
  label: string
  role: string
  email: string
  password: string
}

export const DEMO_ACCOUNTS: DemoAccount[] = [
  {
    label: 'Maria Magda',
    role: 'Super admin',
    email: 'admin@waago.bj',
    password: 'waago-dev-password',
  },
]

/**
 * Whether to offer the one-click demo sign-ins.
 *
 * <p>Shares its answer with the client and provider apps' OTP buttons rather
 * than re-deciding: all three are the same question, and when this was its own
 * `import.meta.env.DEV` the hosted demo stack — which runs `pnpm -r build` —
 * lost the buttons in every app at once and nobody could sign in to show it.
 */
export function demoLoginsEnabled(): boolean {
  return devCodeAvailable()
}

/**
 * The code that was just delivered to `destination`, for development only.
 *
 * <p>Staff sign-in is two factors, so the one-click demo button stopped being
 * one click the day that landed: it bought a challenge and then sat on a screen
 * asking for a code nobody could read. Codes are hashed at rest — correctly —
 * and under `local`/`ci` the senders remember the last one per destination and
 * `/v1/dev/last-code` hands it back. In production neither the bean nor the
 * route exists, so this returns null and the button is not rendered anyway.
 *
 * <p>Polled, because the code is recorded as the mail is sent — a moment after
 * the response the browser already has.
 *
 * @param notBefore epoch milliseconds; a code recorded earlier is ignored and
 *   polling continues. Pass the moment the password was submitted: the log
 *   keeps only the *last* code per address, so a second demo sign-in with the
 *   same account otherwise gets the previous one back instantly — already
 *   consumed, and refused by the screen it was filled into.
 */
export async function fetchDemoCode(
  destination: string,
  notBefore?: number,
): Promise<string | null> {
  // The same base the API client uses — relative by default, so this goes
  // through the dev server's proxy rather than a second hard-coded origin.
  const base = (import.meta.env['VITE_API_BASE'] as string | undefined) ?? '/v1'
  // Slack, because `notBefore` is read off the browser clock and compared
  // against the server's. They agree to well under a second in every
  // environment this runs in, and a stale code is minutes old, not seconds.
  const since = notBefore === undefined ? '' : `&notBefore=${notBefore - 2000}`
  for (let poll = 0; poll < 20; poll++) {
    const res = await fetch(
      `${base}/dev/last-code?destination=${encodeURIComponent(destination)}${since}`,
    )
    if (res.ok) {
      return ((await res.json()) as { code: string }).code
    }
    await new Promise((resolve) => setTimeout(resolve, 150))
  }
  return null
}

export function DemoLogins({
  onPick,
  busy,
}: {
  onPick: (account: DemoAccount) => void
  busy: boolean
}) {
  if (!demoLoginsEnabled()) return null

  return (
    <section aria-labelledby="demo-logins-heading" className="mt-8">
      <div className="mb-3 flex items-center gap-2">
        <span className="h-px flex-1 bg-slate-200" />
        <h2
          id="demo-logins-heading"
          className="text-2xs flex items-center gap-1.5 font-medium tracking-wide text-slate-500 uppercase"
        >
          <Zap className="size-3" aria-hidden="true" />
          Connexion rapide
        </h2>
        <span className="h-px flex-1 bg-slate-200" />
      </div>

      <div className="flex flex-col gap-2">
        {DEMO_ACCOUNTS.map((account) => (
          <Button
            key={account.email}
            variant="outline"
            block
            disabled={busy}
            onClick={() => onPick(account)}
            data-testid={`demo-login-${account.email}`}
            className="justify-between"
          >
            <span className="flex flex-col items-start leading-tight">
              <span className="font-medium text-slate-900">{account.label}</span>
              <span className="text-2xs text-slate-600">{account.email}</span>
            </span>
            <Badge tone="info" size="sm">
              {account.role}
            </Badge>
          </Button>
        ))}
      </div>

      <p className="text-2xs mt-3 text-slate-500">
        Comptes de démonstration — disponibles uniquement en développement.
      </p>
    </section>
  )
}
